import React, { useEffect, useRef, useState } from "react";
import {
  getGmailStatus,
  isGmailReadyForPerformer,
  sendEmailDraft,
  updateEmailDraft,
} from "../../businessModeService";
import { renderUsageOrError, usageOrErrorMessage } from "./UsageLimitBanner";
import { friendlyOutcomeDetail } from "./userFacingErrors";
import { formatImplementedAtLabel, slackSentAtFromResult } from "./aiAssistImplementedAt";
import {
  AI_ICONS,
  AiActionButton,
  AIDraftStatusStrip,
  AIRunDetails,
  CopyDraftButton,
  InlineEditableField,
} from "./AIDraftComponents";
import { startPerformerGmailOAuthFlow } from "../performerGmailOAuth";
import SlackChannelPicker from "./SlackChannelPicker";
import SettingsChipInput from "./SettingsChipInput";
import SendChannelMenu from "./SendChannelMenu";
import ViewChannelMenu, { viewChannelsFromSent } from "./ViewChannelMenu";
import InlineSendConfirm from "./InlineSendConfirm";
import { addEmailChips, joinEmails } from "./emailRecipientChips";
import config from "../../../core/config";

const resolveItemKey = (result) => String(result?.client_item_id || result?.key || "").trim();

/**
 * Hydrate the "already sent via Gmail" UI from a persisted performer result.
 *
 * @example
 * hydrateGmailSentState({ gmail_sent_at: "2026-08-19T11:22:00Z", gmail_sent_message_id: "18abc0d1" });
 * // { sentAt: "2026-08-19T11:22:00Z", messageId: "18abc0d1" }
 */
const hydrateGmailSentState = (result) => {
  const sentAt = String(result?.gmail_sent_at || "").trim();
  const messageId = String(result?.gmail_sent_message_id || "").trim();
  if (!sentAt && !messageId) return null;
  return { sentAt, messageId };
};

/**
 * Hydrate the "already sent via Slack" UI from a persisted performer result.
 *
 * @example
 * hydrateSlackSentState({ slack_sent_at: "2026-08-19T11:22:00Z", slack_permalink: "https://..." });
 * // { sentAt: "2026-08-19T11:22:00Z", permalink: "https://...", messageTs: "" }
 */
const hydrateSlackSentState = (result) => {
  const sentAt = slackSentAtFromResult(result);
  const permalink = String(result?.slack_permalink || "").trim();
  const messageTs = String(result?.slack_message_ts || "").trim();
  if (!sentAt && !permalink && !messageTs) return null;
  return { sentAt, permalink, messageTs };
};

/**
 * Expanded panel for a unified, channel-agnostic message draft.
 *
 * Renders one drafted message (editable inline, local-only) and a single Send
 * control. The Send menu lists delivery channels (Gmail, Slack, …) so adding
 * another medium does not add another toolbar button. Gmail accepts any typed
 * address, including several at once, without searching Gmail history. Slack
 * still searches channels/people after it is chosen.
 *
 * @example
 * // Input: result.type === "message"
 * // Output: draft preview + one Send menu (Gmail / Slack)
 */
const AIMessageExpanded = ({ result, onRetry, retryLoading, onResultPatch, iconOnly = false }) => {
  const itemKey = resolveItemKey(result);

  const [editedSubject, setEditedSubject] = useState(String(result?.subject || ""));
  const [editedBody, setEditedBody] = useState(String(result?.draft || ""));
  // Live text from the subject/body editors, kept in sync on every keystroke (see
  // InlineEditableField's onDraftChange) so "Try again" can pick up an edit that's
  // still in progress -- e.g. clicking retry right after typing, before the field's
  // own blur-commit has landed. Mirrors AIDraftComponents.js's AIDraftExpanded.
  const [pendingSubjectDraft, setPendingSubjectDraft] = useState(null);
  const [pendingBodyDraft, setPendingBodyDraft] = useState(null);
  const [subjectWasEdited, setSubjectWasEdited] = useState(false);
  const [bodyWasEdited, setBodyWasEdited] = useState(false);
  const [gmailSent, setGmailSent] = useState(() => hydrateGmailSentState(result));
  const [slackSent, setSlackSent] = useState(() => hydrateSlackSentState(result));

  const [showGmailPicker, setShowGmailPicker] = useState(false);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [recipientDraft, setRecipientDraft] = useState("");
  const [recipientError, setRecipientError] = useState("");
  const [sendingGmail, setSendingGmail] = useState(false);
  const [gmailError, setGmailError] = useState("");
  const [gmailStatusChecked, setGmailStatusChecked] = useState(false);
  const [gmailBlocked, setGmailBlocked] = useState(null); // null | "connect"
  const [connectingGmail, setConnectingGmail] = useState(false);
  const gmailOauthStopRef = useRef(null);

  const [showSlackPicker, setShowSlackPicker] = useState(false);
  const [slackError, setSlackError] = useState("");

  useEffect(() => {
    return () => gmailOauthStopRef.current?.();
  }, []);

  // Mirrors SlackChannelPicker's checkSendReady: check the connection before
  // showing the recipient input, instead of only surfacing "not connected"
  // after a failed send. Fails open (lets typing proceed) on a status-check
  // error, same as Slack's — a transient check failure shouldn't block send.
  useEffect(() => {
    if (!showGmailPicker || gmailSent) return undefined;
    let cancelled = false;
    setGmailStatusChecked(false);
    void (async () => {
      try {
        const status = await getGmailStatus({ verify: "performer" });
        if (cancelled) return;
        setGmailBlocked(isGmailReadyForPerformer(status) ? null : "connect");
      } catch {
        if (cancelled) return;
        setGmailBlocked(null);
      } finally {
        if (!cancelled) setGmailStatusChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showGmailPicker, gmailSent]);

  const handleConnectGmail = async () => {
    if (connectingGmail) return;
    gmailOauthStopRef.current?.();
    setConnectingGmail(true);
    try {
      const authResp = await fetch(
        `${config.BACKEND_URL}/externalauth/gmail/auth_url?purpose=performer`,
        { method: "GET", credentials: "include" }
      );
      const authPayload = await authResp.json();
      const authUrl = authPayload.auth_url || "";
      if (!authUrl) {
        setConnectingGmail(false);
        return;
      }
      gmailOauthStopRef.current = startPerformerGmailOAuthFlow(
        authUrl,
        () => {
          setConnectingGmail(false);
          setGmailBlocked(null);
          setGmailError("");
        },
        () => setConnectingGmail(false)
      );
    } catch {
      setConnectingGmail(false);
    }
  };

  useEffect(() => {
    setEditedSubject(String(result?.subject || ""));
    setEditedBody(String(result?.draft || ""));
    setGmailSent(hydrateGmailSentState(result));
    setSlackSent(hydrateSlackSentState(result));
    setShowGmailPicker(false);
    setShowSlackPicker(false);
    setRecipientEmails([]);
    setRecipientDraft("");
    setRecipientError("");
    setSendingGmail(false);
    setGmailError("");
    setSlackError("");
    gmailOauthStopRef.current?.();
    setGmailStatusChecked(false);
    setGmailBlocked(null);
    setConnectingGmail(false);
    setPendingSubjectDraft(null);
    setPendingBodyDraft(null);
    setSubjectWasEdited(false);
    setBodyWasEdited(false);
    // Only reset on a genuine identity change (fresh draft / "Try again" re-run).
    // Sent-state fields are deliberately excluded: handleConfirmGmailSend /
    // handleSlackSent already flip gmailSent/slackSent locally right after a
    // successful send, so re-deriving from the result prop here would be redundant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey]);

  const hasDraft = Boolean(editedBody.trim());
  const draftWasEdited =
    subjectWasEdited || bodyWasEdited || pendingSubjectDraft != null || pendingBodyDraft != null;

  // Local-only on purpose (like the rest of this panel — see the module docstring):
  // this draft may end up sent via Slack, not Gmail, and Gmail may not even be
  // connected, so this must not call the tasks/{itemKey}/email-draft endpoint the
  // way AIDraftComponents.js's commitSubjectEdit/commitBodyEdit do for the Gmail-only
  // draft type — that endpoint requires a connected Gmail client and eagerly creates
  // a live Gmail draft as a side effect, which would be wrong before a channel is
  // even chosen. onResultPatch still feeds the edit into the shared aiResultsByItem
  // cache, so it survives this panel remounting (task switch and back) instead of
  // reverting to the original AI draft — the actual Gmail draft only gets created at
  // send time, in handleConfirmGmailSend below.
  const commitSubjectEdit = (nextSubject) => {
    setEditedSubject(nextSubject);
    setSubjectWasEdited(true);
    onResultPatch?.({ subject: nextSubject });
    return Promise.resolve();
  };

  const commitBodyEdit = (nextBody) => {
    setEditedBody(nextBody);
    setBodyWasEdited(true);
    onResultPatch?.({ draft: nextBody });
    return Promise.resolve();
  };

  const handleRetryClick = () => {
    onRetry?.({
      // Prefer whatever's live in the editors over the last-saved value: a click on
      // this button blurs an open editor just before this handler runs, and the
      // resulting commit save is async, so editedBody/editedSubject may not have
      // caught up yet.
      previousDraft: draftWasEdited
        ? String(pendingBodyDraft != null ? pendingBodyDraft : editedBody)
        : "",
      previousSubject: draftWasEdited
        ? String(pendingSubjectDraft != null ? pendingSubjectDraft : editedSubject)
        : "",
    });
  };

  const commitRecipientDraft = (raw = recipientDraft) => {
    const { emails, invalid } = addEmailChips(recipientEmails, raw);
    setRecipientEmails(emails);
    setRecipientDraft("");
    if (invalid.length) {
      setRecipientError(`Not a valid email: ${invalid.join(", ")}`);
      return false;
    }
    setRecipientError("");
    return emails.length > 0;
  };

  const handleOpenChannel = (channelId) => {
    if (!itemKey || sendingGmail) return;
    if (channelId === "gmail" && !gmailSent) {
      setShowSlackPicker(false);
      setShowGmailPicker(true);
      setRecipientError("");
      return;
    }
    if (channelId === "slack" && !slackSent) {
      setShowGmailPicker(false);
      setShowSlackPicker(true);
    }
  };

  const handleConfirmGmailSend = async () => {
    const committed = recipientDraft.trim()
      ? addEmailChips(recipientEmails, recipientDraft)
      : { emails: recipientEmails, invalid: [] };
    if (committed.invalid.length) {
      setRecipientError(`Not a valid email: ${committed.invalid.join(", ")}`);
      return;
    }
    const toEmail = joinEmails(committed.emails);
    if (!toEmail || sendingGmail || !itemKey) return;
    setSendingGmail(true);
    setGmailError("");
    try {
      setRecipientEmails(committed.emails);
      setRecipientDraft("");
      await updateEmailDraft(itemKey, {
        toEmail,
        subject: editedSubject,
        body: editedBody,
      });
      const sent = await sendEmailDraft(itemKey);
      const sentAt = String(sent?.gmail_sent_at || "").trim() || new Date().toISOString();
      const messageId = String(sent?.gmail_message_id || "").trim();
      setGmailSent({ sentAt, messageId });
      setShowGmailPicker(false);
      onResultPatch?.({ gmail_sent_at: sentAt, gmail_sent_message_id: messageId });
    } catch (err) {
      setGmailError(usageOrErrorMessage(err, "Failed to send email"));
    } finally {
      setSendingGmail(false);
    }
  };

  const handleSlackSent = (written) => {
    const sentAt = String(written?.slack_sent_at || "").trim();
    const permalink = String(written?.slack_permalink || "").trim();
    const messageTs = String(written?.slack_message_ts || "").trim();
    setSlackSent({ sentAt, permalink, messageTs });
    setShowSlackPicker(false);
    setSlackError("");
    onResultPatch?.({
      slack_sent_at: sentAt,
      slack_message_ts: messageTs,
      slack_permalink: permalink,
      slack_channel_id: written?.slack_channel_id,
    });
  };

  const statusMeta =
    gmailSent && slackSent
      ? { tone: "success", label: "Sent via Gmail and Slack" }
      : gmailSent
        ? { tone: "success", label: "Sent via Gmail" }
        : slackSent
          ? { tone: "success", label: "Sent via Slack" }
          : hasDraft
            ? { tone: "neutral", label: "Draft ready — not sent" }
            : {
                tone: "error",
                label: "Could not draft a message",
                hint: friendlyOutcomeDetail(result?.outcome_detail) || undefined,
              };

  const lastSentAt = gmailSent?.sentAt || slackSent?.sentAt || "";
  const gmailPreview = recipientDraft.trim()
    ? addEmailChips(recipientEmails, recipientDraft)
    : { emails: recipientEmails, invalid: [] };
  const gmailSendHint = joinEmails(gmailPreview.emails)
    ? `Send to ${joinEmails(gmailPreview.emails)}?`
    : "";
  const canSendGmail = recipientEmails.length > 0 || Boolean(recipientDraft.trim());

  return (
    <div className="ai-draft-expanded ai-crm-expanded">
      <AIDraftStatusStrip
        tone={statusMeta.tone}
        label={statusMeta.label}
        hint={statusMeta.hint}
        implementedAt={lastSentAt ? formatImplementedAtLabel(lastSentAt) : ""}
      />

      {gmailError
        ? renderUsageOrError(gmailError, "p", "ai-draft-expanded__connect-warning")
        : null}
      {slackError && !showSlackPicker
        ? renderUsageOrError(slackError, "p", "ai-draft-expanded__connect-warning")
        : null}

      <div className="ai-draft-expanded__actions ai-draft-expanded__toolbar">
        {hasDraft && !(gmailSent && slackSent) ? (
          <CopyDraftButton showIcon={false} text={editedBody} />
        ) : null}

        <ViewChannelMenu
          iconOnly={iconOnly}
          destinations={viewChannelsFromSent({ gmail: gmailSent, slack: slackSent })}
        />

        <SendChannelMenu
          iconOnly={iconOnly}
          disabled={!hasDraft}
          loading={sendingGmail}
          sent={{ gmail: Boolean(gmailSent), slack: Boolean(slackSent) }}
          onSelect={handleOpenChannel}
        />

        {onRetry ? (
          <AiActionButton
            iconOnly={iconOnly}
            className="ai-draft-expanded__retry"
            icon={AI_ICONS.retry}
            label="Try again"
            title={
              draftWasEdited
                ? "Regenerate — applies your draft edits as revision instructions"
                : "Generate a fresh draft for this action item"
            }
            onClick={handleRetryClick}
            loading={retryLoading}
            disabled={sendingGmail}
          />
        ) : null}
      </div>

      {showGmailPicker && !gmailSent && !gmailStatusChecked ? (
        <div className="ai-draft-expanded__compose" aria-busy="true" aria-label="Send via Gmail">
          <p className="ai-draft-expanded__compose-hint">Checking Gmail…</p>
        </div>
      ) : null}

      {showGmailPicker && !gmailSent && gmailStatusChecked && gmailBlocked ? (
        <div className="ai-draft-expanded__compose" aria-label="Send via Gmail">
          <p className="ai-draft-expanded__connect-warning">
            Gmail is not connected. Connect Gmail to send.
          </p>
          <div className="ai-draft-expanded__compose-actions">
            <button
              type="button"
              className="ai-text-action-btn ai-draft-expanded__recipient-update"
              onClick={() => void handleConnectGmail()}
              disabled={connectingGmail}
            >
              {connectingGmail ? "Opening Gmail…" : "Connect Gmail"}
            </button>
            <button
              type="button"
              className="ai-text-action-btn ai-draft-expanded__recipient-update"
              onClick={() => {
                gmailOauthStopRef.current?.();
                setConnectingGmail(false);
                setShowGmailPicker(false);
              }}
              disabled={connectingGmail}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {showGmailPicker && !gmailSent && gmailStatusChecked && !gmailBlocked ? (
        <div className="ai-draft-expanded__compose" aria-label="Send via Gmail">
          <span className="ai-draft-expanded__compose-label">To</span>
          <div className="ai-draft-expanded__finder">
            {recipientEmails.map((email) => (
              <span key={email} className="settings-chip">
                <span className="settings-chip__text" title={email}>
                  {email}
                </span>
                <button
                  type="button"
                  className="settings-chip__remove"
                  onClick={() =>
                    setRecipientEmails((current) => current.filter((item) => item !== email))
                  }
                  aria-label={`Remove ${email}`}
                >
                  ×
                </button>
              </span>
            ))}
            <SettingsChipInput
              value={recipientDraft}
              onChange={(event) => {
                setRecipientDraft(event.target.value);
                if (recipientError) setRecipientError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
                  if (recipientDraft.trim()) {
                    event.preventDefault();
                    commitRecipientDraft();
                  }
                  return;
                }
                if (event.key === "Backspace" && !recipientDraft && recipientEmails.length) {
                  setRecipientEmails((current) => current.slice(0, -1));
                }
              }}
              onPaste={(event) => {
                const pasted = event.clipboardData?.getData("text") || "";
                if (/[,;\s]/.test(pasted)) {
                  event.preventDefault();
                  commitRecipientDraft(`${recipientDraft}${pasted}`);
                }
              }}
              onBlur={() => {
                if (recipientDraft.trim()) commitRecipientDraft();
              }}
              placeholder={recipientEmails.length ? "Add another" : "Add email"}
              aria-label="Recipient email addresses"
              autoFocus
              autoComplete="off"
            />
          </div>
          <div className="ai-draft-expanded__compose-actions">
            <InlineSendConfirm
              hint={gmailSendHint}
              disabled={sendingGmail || !canSendGmail}
              loading={sendingGmail}
              onArm={() => {
                if (gmailPreview.invalid.length) {
                  setRecipientError(`Not a valid email: ${gmailPreview.invalid.join(", ")}`);
                  return false;
                }
                if (recipientDraft.trim()) commitRecipientDraft();
                return Boolean(gmailSendHint);
              }}
              onConfirm={() => void handleConfirmGmailSend()}
            />
            <button
              type="button"
              className="ai-text-action-btn ai-draft-expanded__recipient-update"
              onClick={() => {
                setShowGmailPicker(false);
                setRecipientError("");
              }}
              disabled={sendingGmail}
            >
              Cancel
            </button>
          </div>
          {recipientError ? (
            <p className="ai-draft-expanded__finder-error">{recipientError}</p>
          ) : null}
        </div>
      ) : null}

      {showSlackPicker && !slackSent ? (
        <SlackChannelPicker
          itemKey={itemKey}
          message={editedBody}
          onCancel={() => setShowSlackPicker(false)}
          onSent={handleSlackSent}
          onError={(msg) => setSlackError(msg)}
        />
      ) : null}

      {editedSubject.trim() ? (
        <p className="ai-draft-expanded__subject">
          <span className="ai-draft-expanded__subject-label">Subject</span>
          {gmailSent && slackSent ? (
            <span className="ai-draft-expanded__subject-text">{editedSubject}</span>
          ) : (
            <InlineEditableField
              value={editedSubject}
              onCommit={commitSubjectEdit}
              onDraftChange={setPendingSubjectDraft}
              placeholder="Subject"
              ariaLabel="Message subject"
              className="ai-inline-edit--subject"
            />
          )}
        </p>
      ) : null}

      {hasDraft ? (
        gmailSent && slackSent ? (
          <pre className="ai-crm-expanded__note-body">{editedBody}</pre>
        ) : (
          <div className="ai-crm-expanded__note-body-edit">
            <InlineEditableField
              value={editedBody}
              onCommit={commitBodyEdit}
              onDraftChange={setPendingBodyDraft}
              placeholder="Drafted message"
              ariaLabel="Drafted message"
              multiline
            />
          </div>
        )
      ) : null}

      <AIRunDetails result={result} />
    </div>
  );
};

export default AIMessageExpanded;
